/**
 * Campaigns — `docs/API.md` §9.
 *
 * Fully implemented server-side: list, create, get, patch, delete, and the
 * member add/list/remove sub-resource — `salescrmapi/src/modules/campaigns`.
 * Targets and forecasts (the rest of §9) are not built.
 *
 * This replaces the read-only `listCampaigns` the Reports screen used to call
 * through `crm.api.js` (a fixture-shaped `{type, spent, leads, roi}` row that
 * predates this module and no longer matches what the real endpoint returns).
 */
import apiClient from './axiosClient.js';
import { withMockFallback } from './mockFallback.js';

/** @param {object} [params] any of listCampaignsQuery's fields, e.g. `{ q, status, owner_id }` */
export function listCampaigns(params = {}) {
  return withMockFallback(
    'GET /campaigns',
    async () => (await apiClient.get('/campaigns', { params })).data,
    () => ({
      data: [],
      page: { limit: params.limit ?? 50, next_cursor: null, prev_cursor: null, has_more: false, total: 0 },
    }),
  );
}

/** One campaign. @param {string} id */
export function getCampaign(id) {
  return withMockFallback(
    `GET /campaigns/${id}`,
    async () => (await apiClient.get(`/campaigns/${id}`)).data,
    () => null,
  );
}

/**
 * Create a campaign. Carries an Idempotency-Key so a double-submit cannot
 * create the same campaign twice.
 * @param {object} payload at minimum `{ code, name }`
 */
export function createCampaign(payload) {
  return withMockFallback(
    'POST /campaigns',
    async () =>
      (await apiClient.post('/campaigns', payload, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })).data,
    () => ({ ...payload, id: crypto.randomUUID(), row_version: 1 }),
  );
}

/**
 * Update a campaign under optimistic locking (§1.7).
 * @param {string} id @param {object} patch @param {number} rowVersion
 */
export function updateCampaign(id, patch, rowVersion) {
  return withMockFallback(
    `PATCH /campaigns/${id}`,
    async () =>
      (await apiClient.patch(`/campaigns/${id}`, patch, {
        headers: { 'If-Match': `"${rowVersion}"` },
      })).data,
    () => ({ id, ...patch }),
  );
}

/**
 * Soft-delete a campaign. No seeded role below superadmin can actually call
 * this — see `campaigns.model.md` §3 — but the client doesn't need to know
 * that; a `403` renders the same as any other permission error.
 * @param {string} id @param {{reason:string, rowVersion?:number}} options
 */
export function deleteCampaign(id, { reason, rowVersion } = {}) {
  return withMockFallback(
    `DELETE /campaigns/${id}`,
    async () =>
      (await apiClient.delete(`/campaigns/${id}`, {
        data: { reason, ...(rowVersion ? { row_version: rowVersion } : {}) },
      })).data,
    () => ({ id, deleted: true }),
  );
}

/** Who's targeted, and how far they've progressed. @param {string} id @param {object} [params] */
export function listCampaignMembers(id, params = {}) {
  return withMockFallback(
    `GET /campaigns/${id}/members`,
    async () => (await apiClient.get(`/campaigns/${id}/members`, { params })).data,
    () => ({ data: [], page: { limit: 50, next_cursor: null, prev_cursor: null, has_more: false } }),
  );
}

/**
 * @param {string} id
 * @param {{lead_ids?:string[], contact_ids?:string[], status?:string}} payload
 * @returns {Promise<{added:number, skipped_duplicates:number, skipped_opted_out:number}>}
 */
export function addCampaignMembers(id, payload) {
  return withMockFallback(
    `POST /campaigns/${id}/members`,
    async () => (await apiClient.post(`/campaigns/${id}/members`, payload)).data,
    () => ({ added: 0, skipped_duplicates: 0, skipped_opted_out: 0 }),
  );
}

/** @param {string} id @param {string} memberId */
export function removeCampaignMember(id, memberId) {
  return withMockFallback(
    `DELETE /campaigns/${id}/members/${memberId}`,
    async () => (await apiClient.delete(`/campaigns/${id}/members/${memberId}`)).data,
    () => ({ deleted: true }),
  );
}
